import { useState } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { useOrganization, useUser } from '@clerk/react';
import { GoogleDocsIcon } from '@/components/ui/icons/google-product-icons';
import { AgentCard } from '@/components/agent-chat/agent-card.component';
import {
  CardFooter,
  CardMeta,
  CardStatus,
} from '@/components/agent-chat/card-parts.component';
import { followingUserText } from '@/components/agent-chat/question-card.util';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import {
  googlePickError,
  useGooglePicker,
} from '@/hooks/use-google-picker.hook';
import type { PickedFile } from '@/lib/google-picker';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAiChatContext,
  sendAiChatMessage,
} from '@/store/features/ui.feature';
import { ConnectGoogleDocs } from '@/containers/playbooks/custom-builder/config/google-doc-trigger-config.component';
import {
  MAX_GOOGLE_DOCS,
  docIdOf,
  folderIdOf,
  googleDocUrl,
  googleFolderUrl,
} from '@/containers/playbooks/custom-builder/config/google-doc.util';

const DOCS_LOGO = (
  <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background'>
    <GoogleDocsIcon className='size-[12px]' />
  </div>
);

/**
 * Clu's Google Docs pick (`request_doc_pick`, display `google_doc_pick`): the
 * member picks with Google the docs a Google Docs trigger watches (kind
 * `docs`) or the folder a Create a Google Doc step writes to (kind `folder`).
 * Picking is what grants Cleanlist drive.file access to them; the pasted ones
 * are offered first. The reply names each picked one by its link. Hidden
 * unless it is this agent's chat with Agents unlocked (hidden while it loads).
 */
export function GoogleDocPickCard({
  result,
  toolCallId,
}: {
  result: Record<string, unknown>;
  toolCallId: string;
}) {
  const context = useAppSelector(selectAiChatContext);
  const { organization, isLoaded } = useOrganization();
  const { user, isLoaded: userLoaded } = useUser();
  const { unlocked } = useAgentsAccess();
  // Two plain values, not one object: useAuiState is useSyncExternalStore,
  // and a new object on every read never settles ("Maximum update depth").
  const state = {
    message: useAuiState((aui) => aui.message.id),
    available: useAuiState((aui) => {
      const tools = aui.message.content.filter(
        (part) => part.type === 'tool-call'
      );
      return (
        followingUserText(aui.thread.messages, aui.message.id) === null &&
        aui.thread.messages.at(-1)?.id === aui.message.id &&
        tools.at(-1)?.toolCallId === toolCallId
      );
    }),
  };
  const enabled =
    unlocked &&
    isLoaded &&
    userLoaded &&
    !!organization &&
    !!user &&
    context?.type === 'agent' &&
    !!context.agent_id &&
    result.agent_id === context.agent_id;
  if (!enabled) return null;
  return (
    <PickDocsCard
      key={`${state.message}:${toolCallId}`}
      result={result}
      available={state.available}
    />
  );
}

function pastedIds(result: Record<string, unknown>, folder: boolean) {
  const ids = Array.isArray(result.file_ids) ? result.file_ids : [];
  const parse = folder ? folderIdOf : docIdOf;
  return ids
    .map((id) => parse(id))
    .filter((id): id is string => !!id)
    .slice(0, folder ? 1 : MAX_GOOGLE_DOCS);
}

/** The reply that names each picked file by its link, as one line. */
function pickedLine(files: PickedFile[], folder: boolean): string {
  const clean = (name: string | undefined, fallback: string) =>
    (name || fallback).replace(/\s+/g, ' ').trim();
  if (folder) {
    const [file] = files;
    return `Picked folder ${clean(file.name, 'Google Drive folder')}: ${googleFolderUrl(file.id)}`;
  }
  return `Picked Google Docs: ${files
    .map((file) => `${clean(file.name, 'Google Doc')} ${googleDocUrl(file.id)}`)
    .join(', ')}`;
}

function PickDocsCard({
  result,
  available,
}: {
  result: Record<string, unknown>;
  available: boolean;
}) {
  const dispatch = useAppDispatch();
  const picker = useGooglePicker('google_docs');
  const folder = result.kind === 'folder';
  const pasted = pastedIds(result, folder);
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const [error, setError] = useState<string | null>(null);

  /** False unlocks the button: nothing was picked. */
  const pick = async () => {
    if (!available || !picker.connected || picked.length) return false;
    setError(null);
    try {
      const files = folder
        ? [await picker.pickFolder(pasted[0])].filter(
            (file): file is PickedFile => !!file
          )
        : (await picker.pickDocs(pasted.length ? pasted : undefined)).slice(
            0,
            MAX_GOOGLE_DOCS
          );
      if (!files.length) return false;
      setPicked(files);
      dispatch(sendAiChatMessage(pickedLine(files, folder)));
      return true;
    } catch (err) {
      setError(googlePickError(err));
      return false;
    }
  };

  const title = picked.length
    ? picked.map((file) => file.name).join(', ')
    : folder
      ? 'Pick a Drive folder'
      : 'Pick Google Docs';
  const status = picked.length ? (
    <CardStatus tone='success'>Selected</CardStatus>
  ) : !available ? (
    <CardStatus>Passed over</CardStatus>
  ) : undefined;
  const connect = !picker.connected && !picked.length;
  const action = !connect && available && !picked.length;
  return (
    <AgentCard
      data-google-doc-pick
      leading={DOCS_LOGO}
      title={title}
      trailing={status}
    >
      {connect || action || error ? (
        <>
          {connect ? <ConnectGoogleDocs /> : null}
          {action ? (
            <CardFooter
              control={
                <CardMeta className='min-w-0'>Opens with Google</CardMeta>
              }
              primary={{
                label: folder ? 'Pick a folder' : 'Pick docs',
                workingLabel: 'Opening',
                onClick: pick,
              }}
            />
          ) : null}
          {error ? (
            <div role='alert'>
              <CardMeta tone='danger'>{error}</CardMeta>
            </div>
          ) : null}
        </>
      ) : null}
    </AgentCard>
  );
}
